import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts'

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

function WorkoverAnalytics() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        setLoading(true)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/workovers/analytics`)
            if (!res.ok) throw new Error('Failed to fetch analytics')
            setData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="space-y-4">
                <button onClick={() => navigate('/workovers')}
                    className={`text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    &larr; Back to Workovers
                </button>
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p>{error}</p>
                </div>
            </div>
        )
    }

    const monthly = data?.monthlySpend || []
    const byType = data?.byJobType || []
    const topWells = data?.topWells || []

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={`border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <button onClick={() => navigate('/workovers')}
                    className={`text-sm mb-2 inline-block ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                    &larr; Back to Workovers
                </button>
                <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    Workover Analytics
                </h1>
            </div>

            {/* Monthly Spend Chart */}
            <div className="chart-container">
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                    Monthly Spend (Last 12 Months)
                </h2>
                {monthly.length > 0 ? (
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthly} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="monthName"
                                    tick={{ fontSize: 12, fill: '#94a3b8' }} stroke="#475569"
                                    tickFormatter={(m) => m?.substring(0, 3)}
                                />
                                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} stroke="#475569"
                                    tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                                />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#e2e8f0' }}
                                    formatter={(v) => [`$${v.toLocaleString()}`, '']}
                                />
                                <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                                <Bar dataKey="totalSpend" fill="#3b82f6" name="Total Spend" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No spend data available</p>
                )}
            </div>

            {/* By Job Type + Top Wells side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* By Job Type */}
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Spend by Job Type (Last Year)
                    </h2>
                    {byType.length > 0 ? (
                        <>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={byType} dataKey="totalSpend" nameKey="jobType"
                                            cx="50%" cy="50%" outerRadius={80} label={({ jobType, percentage }) => `${jobType} (${percentage}%)`}
                                            labelLine={{ stroke: '#94a3b8' }}>
                                            {byType.map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#e2e8f0' }}
                                            formatter={(v) => `$${v.toLocaleString()}`}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4 overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th className="text-right">Count</th>
                                            <th className="text-right">Total</th>
                                            <th className="text-right">Avg Cost</th>
                                            <th className="text-right">Avg Days</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byType.map((t, i) => (
                                            <tr key={i}>
                                                <td className="font-medium">{t.jobType || 'Unknown'}</td>
                                                <td className="text-right">{t.count}</td>
                                                <td className="text-right font-mono">${t.totalSpend.toLocaleString()}</td>
                                                <td className="text-right font-mono">${t.avgCost.toLocaleString()}</td>
                                                <td className="text-right">{t.avgDays}d</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No data</p>
                    )}
                </div>

                {/* Top Wells by Cost */}
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Top Wells by Workover Spend (Last Year)
                    </h2>
                    {topWells.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Well</th>
                                        <th className="text-right">WOs</th>
                                        <th className="text-right">Total Spend</th>
                                        <th>Last WO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topWells.map((w, i) => (
                                        <tr key={i}>
                                            <td className={`font-mono ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{i + 1}</td>
                                            <td className="font-medium">{w.wellName}</td>
                                            <td className="text-right">{w.workoverCount}</td>
                                            <td className="text-right font-mono">${w.totalSpend.toLocaleString()}</td>
                                            <td className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                                {w.lastWorkover ? new Date(w.lastWorkover).toLocaleDateString() : '--'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No data</p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default WorkoverAnalytics
